import React, { useCallback, useEffect, useState } from 'react';
import {
  BuyerContact,
  CollectionActivityReference,
  extractPlainTextFromLexical as extractPlainText,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { createCollectionActivity } from '../../../../../../redux/services/collectionActivityService';
import LexicalEditor from '../../../../../general/LexicalEditor';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';
import AppFilePicker from '../../../../../general/AppFilePicker/AppFilePicker';

interface AddNoteModalProps {
  open: boolean;
  buyerId: string;
  onClose: () => void;
  onSuccess?: () => void;
  initialReferences?: CollectionActivityReference[];
  contacts?: BuyerContact[];
}

function AddNoteModal({
  open,
  buyerId,
  onClose,
  onSuccess,
  initialReferences,
  contacts,
}: AddNoteModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [noteContent, setNoteContent] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  const isLoading = loadingList.includes('createCollectionActivity');

  useEffect(() => {
    if (open && initialReferences) {
      setReferences(initialReferences);
    }
  }, [open, initialReferences]);

  const handleEditorChange = useCallback(
    (content: string) => {
      setNoteContent(content);
      if (noteError) setNoteError('');
    },
    [noteError]
  );

  const handleSubmit = async () => {
    if (!extractPlainText(noteContent)) {
      setNoteError('Please enter note content');
      return;
    }

    if (!currentSupplier?.id) {
      return;
    }

    if (!user?.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(
        createCollectionActivity({
          supplierId: currentSupplier.id,
          activityData: {
            supplierId: currentSupplier.id,
            buyerId,
            activityType: 'NOTE',
            source: 'MANUAL',
            performedBy: user.id,
            date: new Date(),
            noteDetails: {
              content: JSON.parse(noteContent),
            },
            references: references.length > 0 ? references : undefined,
          },
          noteAttachmentsFiles: attachmentFiles,
        })
      );

      if (result.payload && (result.payload as any).success) {
        setNoteContent('');
        setReferences([]);
        setAttachmentFiles([]);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to create note:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to create note. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setNoteContent('');
      setReferences([]);
      setNoteError('');
      setAttachmentFiles([]);
      onClose();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Add Note"
      maxWidth="576px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            variant="secondary"
            isDisabled={isLoading}
            label="Cancel"
          />
          <DillButton
            onClick={handleSubmit}
            variant="primary"
            isLoading={isLoading}
            label="Save Note"
          />
        </>
      }>
      <LexicalEditor
        label="Note Content"
        isRequired
        onChange={handleEditorChange}
        placeholder="Enter your note..."
        minHeight="120px"
        editable={!isLoading}
        errorText={noteError}
      />
      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={buyerId}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        contacts={contacts}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Attachments (Optional)
        </label>
        <AppFilePicker
          onSelectFile={(files: File[]) => {
            setAttachmentFiles(files);
          }}
        />
      </div>
    </DillModal>
  );
}

export default AddNoteModal;
